import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_LOGIN_BG = 'https://images.unsplash.com/photo-1640631005057-de521cfcd36e?crop=entropy&cs=srgb&fm=jpg&q=85';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [bgImage, setBgImage] = useState(DEFAULT_LOGIN_BG);
  const { login, sessionExpired, setSessionExpired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const expiredMsg = sessionExpired || location.state?.message;

  useEffect(() => {
    axios.get(`${BACKEND}/api/settings/login-appearance`)
      .then(res => {
        const url = res.data?.background_url;
        if (url) setBgImage(url.startsWith('http') ? url : `${BACKEND}${url}`);
      })
      .catch(() => { /* keep default background */ });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Zalogowano pomyślnie');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd logowania');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-cover bg-center relative p-4"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <div className="absolute inset-0 bg-black/50" />
      
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-[var(--crm-bg-dialog)] border border-[var(--crm-border)] rounded-lg p-6 md:p-8 shadow-xl">
          <div className="mb-6 text-center">
            <img src="/logo.png" alt="Trendhomes" className="h-20 mx-auto mb-4 dark:brightness-100 brightness-0" />
            <p className="text-[var(--crm-text-secondary)]">Zarządzanie Projektami</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {expiredMsg && (
              <div className="bg-amber-500/15 border border-amber-500/30 text-amber-300 text-sm p-3 rounded-lg" data-testid="session-expired-msg">
                {typeof expiredMsg === 'string' ? expiredMsg : 'Sesja wygasła. Zaloguj się ponownie.'}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[var(--crm-text-secondary)]">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="twoj@email.pl"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                required
                className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-white focus:ring-[#FF6B35] focus:border-[#FF6B35]"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[var(--crm-text-secondary)]">Hasło</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-[#FF6B35] hover:text-[#E85D2E]"
                  data-testid="forgot-password-link"
                >
                  Zapomniałeś hasła?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                required
                className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-white focus:ring-[#FF6B35] focus:border-[#FF6B35]"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white font-semibold"
            >
              {loading ? 'Logowanie...' : 'Zaloguj się'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};